<script setup lang="ts">
import { computed, useSlots } from 'vue';

interface ActivityProductCardLayoutProps {
  shouldHideProductLink: boolean;
}

const props = defineProps<ActivityProductCardLayoutProps>();

const slots = useSlots();
const hasHeader = computed(() => !!slots['header']);
</script>

<template>
  <div
    class="activity-product-card-layout"
    :class="{ 'is-experiment': props.shouldHideProductLink }"
  >
    <slot name="header" />
    <div
      :class="[
        'product-content',
        {
          'has-header': hasHeader,
        },
      ]"
    >
      <div class="image">
        <slot name="image" />
        <slot name="tag" />
      </div>
      <div class="content" :class="{ 'has-header': hasHeader }">
        <slot name="content" />
      </div>
      <div class="trash-icon">
        <slot name="trash-icon" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.activity-product-card-layout {
  --min-height-of-image-desktop: 205px;
  --trash-icon-spacing: var(--spacing-16);

  @include for-less-or-equal-than-599-px {
    --trash-icon-spacing: 0;
  }

  border-radius: var(--border-radius-8);
  border: 1px solid var(--color-neutral-30);
  overflow: hidden;
  position: relative;

  .product-content {
    width: 100%;
    background: var(--color-white);
    display: grid;
    grid-template-columns: 3fr 5fr;
    grid-template-rows: minmax(var(--min-height-of-image-desktop), auto);

    position: relative;
    gap: var(--spacing-12);
    padding: var(--spacing-16);

    @include for-less-or-equal-than-599-px {
      padding: 0;
    }

    @include on-hover {
      background: var(--color-neutral-30);
      transition: background var(--transition-short);
      cursor: pointer;
    }

    .image {
      grid-column: 1/2;
      width: 100%;
      cursor: pointer;
      position: relative;
      border-radius: var(--border-radius-8);

      overflow: hidden;
      // NOTE: Next two rules are forcing image to not be highier
      // than .content column
      height: 0;
      min-height: 100%;

      @include for-less-or-equal-than-599-px {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    .content {
      grid-column: 2/3;
    }

    .trash-icon {
      position: absolute;
      top: var(--trash-icon-spacing);
      right: var(--trash-icon-spacing);
    }
  }
  &.is-experiment {
    .product-content {
      @include on-hover {
        cursor: auto;
        transition: none;
        background: var(--color-white);
      }
    }
  }
  .product-content.has-header {
    border-top: 1px solid var(--color-neutral-30);
  }

  @include for-more-or-equal-than-1200-px {
    .content {
      min-height: 220px;
    }
  }
  @include for-less-or-equal-than-599-px {
    .product-content {
      grid-template-rows: repeat(3, auto);

      // FYI: should be auto, but firefox mobile on android have issues with it
      grid-template-columns: 1fr;

      .image {
        grid-column: 1/4;
        width: 100%;
        height: auto;
        aspect-ratio: 16/9;
        overflow: hidden;
      }

      .content {
        grid-column: 1/4;
        border-bottom: unset;
        border-bottom-left-radius: unset;
      }

      .footer {
        grid-column: 1/4;
        border: unset;
        border-bottom-left-radius: var(--border-radius-8);
        border-top-right-radius: unset;
      }
    }
  }
}
</style>
